import { useState, type ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Gauge,
  Languages,
  LogOut,
  Moon,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import type { User } from 'firebase/auth';
import type { OrgRole } from '@jam/shared';
import { useAuth } from '@/components/providers/auth-provider';
import { useUiStore } from '@/stores/ui-store';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export function ProfileMenu() {
  const { user, activeOrg, signOutUser } = useAuth();
  const role: OrgRole | null = activeOrg?.role ?? null;
  const { lang, theme, density, toggleTheme, toggleDensity, setLang } =
    useUiStore();

  if (!user) return null;

  const displayName = user.displayName?.trim() || user.email || '';
  const initials = getInitials(user);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('menu.account', lang)}
          title={displayName}
          className={cn(
            'relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-pill border border-[var(--color-border)] bg-[var(--color-elevated)] text-xs font-medium text-[var(--color-ink)] transition-colors',
            'hover:border-[var(--color-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-0',
            'data-[state=open]:border-[var(--color-accent)] data-[state=open]:ring-2 data-[state=open]:ring-[var(--color-accent)]',
          )}
        >
          <Avatar user={user} initials={initials} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          align="end"
          className="z-50 min-w-[240px] rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm shadow-md"
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-pill border border-[var(--color-border)] bg-[var(--color-elevated)] text-xs font-medium text-[var(--color-ink)]">
              <Avatar user={user} initials={initials} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-[var(--color-ink)]">
                {displayName}
              </div>
              {user.email && user.email !== displayName && (
                <div className="truncate font-mono text-xxs text-[var(--color-ink-muted)]">
                  {user.email}
                </div>
              )}
              {role && <RoleBadge role={role} />}
            </div>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          <MenuRow
            icon={Gauge}
            label={t('menu.density', lang)}
            value={density}
            onSelect={toggleDensity}
          />
          <MenuRow
            icon={Languages}
            label={t('menu.language', lang)}
            value={lang === 'fa' ? 'فارسی' : 'English'}
            onSelect={() => setLang(lang === 'fa' ? 'en' : 'fa')}
          />
          <MenuRow
            icon={theme === 'midnight' ? Sun : Moon}
            label={t('menu.theme', lang)}
            value={t(`theme.${theme}`, lang)}
            onSelect={() => toggleTheme()}
          />

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          <DropdownMenu.Item
            onSelect={() => signOutUser()}
            className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
          >
            <LogOut className="size-4 icon-directional" aria-hidden />
            <span>{t('auth.signout', lang)}</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function MenuRow({
  icon: Icon,
  label,
  value,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        // Keep menu open so users can cycle through values without reopening.
        e.preventDefault();
        onSelect();
      }}
      className="flex cursor-pointer items-center gap-2 rounded-xs px-2 py-1.5 text-sm text-[var(--color-ink)] outline-none data-[highlighted]:bg-[var(--color-elevated)]"
    >
      <Icon className="size-4" aria-hidden />
      <span className="flex-1">{label}</span>
      <span className="font-mono text-xxs uppercase tracking-wider text-[var(--color-ink-muted)]">
        {value}
      </span>
    </DropdownMenu.Item>
  );
}

function RoleBadge({ role }: { role: OrgRole }) {
  return (
    <span className="mt-0.5 inline-block rounded-xs bg-[var(--color-accent-soft)] px-1 py-0.5 font-mono text-xxs uppercase tracking-wider text-[var(--color-accent)]">
      {role}
    </span>
  );
}

function Avatar({ user, initials }: { user: User; initials: string }) {
  const [errored, setErrored] = useState(false);
  if (user.photoURL && !errored) {
    return (
      <img
        src={user.photoURL}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
        className="size-full object-cover"
      />
    );
  }
  return <span aria-hidden>{initials}</span>;
}

function getInitials(user: User): string {
  const source = user.displayName?.trim() || user.email || '';
  if (!source) return '?';
  const parts = source
    .replace(/@.*/u, '')
    .split(/[\s._-]+/u)
    .filter(Boolean);
  if (parts.length === 0) return source.slice(0, 1).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || source.slice(0, 1).toUpperCase();
}
